import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import * as enums from '@playbook-technologies/common-enums';
import {
  Async,
  Dispatchers,
  toAsyncError,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { Actions } from '@client/new/helpers/types';
import {
  useProfileModel,
  checkCustomUrlAvailability,
} from '../model/Profile.model';
import { useSaveProfileModel } from '../model/SaveProfile.model';
import type { ProfileSaveActor } from '../model/SaveProfile.model';
import {
  ProfileDomain,
  SectionCompletion,
  SectionId,
} from '../model/Profile.types';

export type { SectionCompletion } from '../model/Profile.types';
import { profileSchema, ProfileFormValues } from './Profile.schema';
import { calculateAllCompletions } from './Profile.completion';
import { useAiAssistVM, AiAssistVM } from './AiAssist.viewModel';

type ProfileVMActionMap = {
  Save: null;
};

type ProfileCurrentUser = {
  influencerId?: number | null;
  role?: number | null;
};

export type ProfileVMParams = {
  routeInfluencerId?: string;
  currentUser: ProfileCurrentUser | null;
  fallbackInfluencerId?: number | null;
};

type ProfileEditorContext =
  | { type: 'editable'; creatorId: number; actor: ProfileSaveActor }
  | {
      type: 'unavailable';
      reason:
        | 'invalid-route-influencer-id'
        | 'missing-current-user'
        | 'missing-creator-id'
        | 'unauthorized';
    };

type SaveState = { type: 'idle' } | { type: 'saving' };

type CustomUrlState = {
  value: string;
  isEditable: boolean;
  validate: (url: string) => Promise<[boolean, string?]>;
  onChange: (value: string) => void;
};

type ProfileVMData = {
  profile: ProfileDomain;
  isLive: boolean;
  isSaving: boolean;
  completions: Record<SectionId, SectionCompletion>;
  formValues: ProfileFormValues;
  aiAssist: AiAssistVM;
  customUrl: CustomUrlState;
  form: {
    register: ReturnType<typeof useForm<ProfileFormValues>>['register'];
    errors: ReturnType<
      typeof useForm<ProfileFormValues>
    >['formState']['errors'];
    isDirty: boolean;
    handleSubmit: ReturnType<typeof useForm<ProfileFormValues>>['handleSubmit'];
    setValue: ReturnType<typeof useForm<ProfileFormValues>>['setValue'];
    watch: ReturnType<typeof useForm<ProfileFormValues>>['watch'];
    control: ReturnType<typeof useForm<ProfileFormValues>>['control'];
  };
};

export type ProfileVM = ProfileVMData & Dispatchers<ProfileVMActionMap>;

const adminProfileRoles = new Set<number>([
  enums.PortalUser.Role.Administrator.value,
  enums.PortalUser.Role.Moderator.value,
  enums.PortalUser.Role.ContentEditor.value,
]);

const toRouteCreatorId = (routeInfluencerId: string | undefined) => {
  if (!routeInfluencerId) return null;

  const routeCreatorId = Number(routeInfluencerId);
  return Number.isInteger(routeCreatorId) && routeCreatorId > 0
    ? routeCreatorId
    : undefined;
};

const isAdminProfileRole = (role: number | null | undefined) =>
  typeof role === 'number' && adminProfileRoles.has(role);

export const toProfileEditorContext = (
  params: ProfileVMParams,
): ProfileEditorContext => {
  if (!params.currentUser) {
    return { type: 'unavailable', reason: 'missing-current-user' };
  }

  const routeCreatorId = toRouteCreatorId(params.routeInfluencerId);

  if (routeCreatorId === undefined) {
    return { type: 'unavailable', reason: 'invalid-route-influencer-id' };
  }

  const currentUserInfluencerId = params.currentUser.influencerId ?? null;
  const creatorId =
    routeCreatorId ?? currentUserInfluencerId ?? params.fallbackInfluencerId;

  if (!creatorId) {
    return { type: 'unavailable', reason: 'missing-creator-id' };
  }

  if (creatorId === currentUserInfluencerId) {
    return { type: 'editable', creatorId, actor: 'creator' };
  }

  if (isAdminProfileRole(params.currentUser.role)) {
    return { type: 'editable', creatorId, actor: 'admin' };
  }

  return { type: 'unavailable', reason: 'unauthorized' };
};

export const toProfileCurrentUser = (
  user: unknown,
): ProfileCurrentUser | null => {
  if (!user || typeof user !== 'object') return null;

  const values = user as Record<string, unknown>;
  const role = values.role;
  const influencerId = values.influencerId;

  if (typeof role !== 'number' || !Number.isFinite(role)) return null;

  if (influencerId === null || influencerId === undefined) {
    return { role };
  }

  if (typeof influencerId !== 'number' || !Number.isFinite(influencerId)) {
    return null;
  }

  return { influencerId, role };
};

const toFormDefaults = (profile: ProfileDomain): ProfileFormValues => ({
  firstName: profile.firstName,
  lastName: profile.lastName,
  title: profile.title,
  gender: profile.gender,
  customColorSchema: profile.customColorSchema,
  profileInstagram: profile.profileInstagram,
  profileTiktok: profile.profileTiktok,
  profileYoutubeChannel: profile.profileYoutubeChannel,
  messageProfile: profile.messageProfile,
  accomplishments: profile.accomplishments,
  mediaAvatar: profile.mediaAvatar as ProfileFormValues['mediaAvatar'],
  mediaCover: profile.mediaCover as ProfileFormValues['mediaCover'],
  mediaBanner: profile.mediaBanner as ProfileFormValues['mediaBanner'],
  mediaWelcome: profile.mediaWelcome as ProfileFormValues['mediaWelcome'],
  keywords: profile.keywords,
});

const useCustomUrl = (profile: ProfileDomain | null) => {
  const originalUrl = profile?.customURL ?? '';
  const [customURL, setCustomURL] = useState(originalUrl);
  const requestIdRef = useRef(0);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (originalUrl && !initializedRef.current) {
      setCustomURL(originalUrl);
      initializedRef.current = true;
    }
  }, [originalUrl]);

  const isEditable =
    !!profile && profile.mode !== 'Live' && profile.mode !== 'RunOff';

  const handleChange = useCallback((value: string) => {
    if (value.length > 30) return;
    if (/^[a-zA-Z0-9-_]*$/.test(value)) {
      setCustomURL(value);
    }
  }, []);

  const validate = useCallback(
    async (url: string): Promise<[boolean, string?]> => {
      const thisRequestId = ++requestIdRef.current;
      const isServerAvailable = await checkCustomUrlAvailability(url);

      if (thisRequestId !== requestIdRef.current) return [true];

      const isAvailable = isServerAvailable || url === originalUrl;
      return [
        isAvailable,
        isAvailable ? '' : 'This name is already in use. Please change it.',
      ];
    },
    [originalUrl],
  );

  return { value: customURL, isEditable, validate, onChange: handleChange };
};

export const useProfileVM = (params: ProfileVMParams): Async<ProfileVM> => {
  const editorContext = toProfileEditorContext(params);
  const creatorId =
    editorContext.type === 'editable' ? editorContext.creatorId : 0;
  const actor =
    editorContext.type === 'editable' ? editorContext.actor : 'creator';
  const model = useProfileModel(creatorId);
  const [saveState, setSaveState] = useState<SaveState>({ type: 'idle' });
  const aiAssist = useAiAssistVM(creatorId);

  const profileData = model.type === 'fetched' ? model.value.profile : null;
  const customUrl = useCustomUrl(profileData);

  const isLive =
    model.type === 'fetched' && model.value.profile.mode === 'Live';

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    values:
      model.type === 'fetched'
        ? toFormDefaults(model.value.profile)
        : undefined,
  });

  const originalCustomURL =
    model.type === 'fetched' ? model.value.profile.customURL : '';

  const { saveProfile } = useSaveProfileModel({
    creatorId,
    customURL: customUrl.value,
    originalCustomURL,
    actor,
  });

  const watchedValues = form.watch();

  const completions = useMemo(
    () => calculateAllCompletions(watchedValues),
    [watchedValues],
  );

  if (editorContext.type !== 'editable') {
    return toAsyncError('Profile unavailable');
  }

  if (model.type !== 'fetched') return model;

  const _dispatch = (action: Actions<ProfileVMActionMap>) => {
    switch (action.type) {
      case 'Save':
        if (saveState.type === 'saving') return;

        form.handleSubmit(
          async (values) => {
            setSaveState({ type: 'saving' });
            try {
              await saveProfile(values);
              toast.success('Profile saved!');
              form.reset(values);
            } catch (err) {
              const message =
                err instanceof Error ? err.message : 'Error saving profile';
              toast.error(message);
            } finally {
              setSaveState({ type: 'idle' });
            }
          },
          // Without this branch, a failed zod validation would make Save appear broken — the click resolves to a no-op with no user feedback. sc-111242
          () => {
            toast.error('Please fix the highlighted fields before saving.');
          },
        )();
        break;
    }
  };

  return toAsyncFetched({
    profile: model.value.profile,
    isLive,
    isSaving: saveState.type === 'saving',
    completions,
    formValues: watchedValues as ProfileFormValues,
    aiAssist,
    customUrl,
    form: {
      register: form.register,
      errors: form.formState.errors,
      isDirty: form.formState.isDirty,
      handleSubmit: form.handleSubmit,
      setValue: form.setValue,
      watch: form.watch,
      control: form.control,
    },
    ...getDispatchers<ProfileVMActionMap>(_dispatch),
  });
};
